import type { ReactElement } from 'react';
import { Pencil, Send, Trash2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  DataTableHeaderRow,
  DataTableTable,
  DataTableTbody,
  DataTableTd,
  DataTableTh,
  DataTableThead,
  DataTableTr,
} from '@/components/ui/data-table';

import type { EmailServerViewModel } from '../api';
import { EmailServerStatusBadge } from './EmailServerStatusBadge';

export function AdminEmailServersTable({
  rows,
  onTest,
  onEdit,
  onDelete,
  pendingDeleteId,
}: {
  readonly rows: readonly EmailServerViewModel[];
  readonly onTest: (row: EmailServerViewModel) => void;
  readonly onEdit: (row: EmailServerViewModel) => void;
  readonly onDelete: (row: EmailServerViewModel) => void;
  readonly pendingDeleteId: string | null;
}): ReactElement {
  const { t } = useTranslation();
  const col = 'routes.admin.email.servers.table';

  return (
    <DataTableTable aria-label={t('routes.admin.email.servers.heading')}>
      <DataTableThead className="sticky top-0 z-10 bg-card">
        <DataTableHeaderRow>
          <DataTableTh className="h-[40px] py-0">{t(`${col}.name`)}</DataTableTh>
          <DataTableTh className="h-[40px] py-0">{t(`${col}.fromAddress`)}</DataTableTh>
          <DataTableTh className="h-[40px] py-0" widthClassName="w-[8rem]">
            {t(`${col}.region`)}
          </DataTableTh>
          <DataTableTh className="h-[40px] py-0" widthClassName="w-[7rem]">
            {t(`${col}.status`)}
          </DataTableTh>
          <DataTableTh className="h-[40px] py-0" widthClassName="w-[8rem]">
            {t(`${col}.actions`)}
          </DataTableTh>
        </DataTableHeaderRow>
      </DataTableThead>
      <DataTableTbody>
        {rows.map((row) => (
          <DataTableTr key={row.id} className="even:bg-muted/55">
            <DataTableTd className="h-[40px] py-0 text-foreground/90" dataOffset>
              <span className="inline-flex items-center gap-2">
                {row.name}
                {row.isDefault && <Badge variant="default">{t(`${col}.defaultBadge`)}</Badge>}
                {!row.active && <Badge variant="outline">{t(`${col}.inactiveBadge`)}</Badge>}
              </span>
            </DataTableTd>
            <DataTableTd className="h-[40px] py-0 text-muted-foreground" dataOffset={false}>
              {row.fromAddress}
            </DataTableTd>
            <DataTableTd className="h-[40px] py-0 font-mono text-xs text-muted-foreground" dataOffset={false}>
              {row.region}
            </DataTableTd>
            <DataTableTd className="h-[40px] py-0" dataOffset={false}>
              <EmailServerStatusBadge serverId={row.id} />
            </DataTableTd>
            <DataTableTd className="h-[40px] py-0" dataOffset={false}>
              <div className="flex items-center gap-1">
                <Button
                  size="icon"
                  variant="ghost"
                  aria-label={t(`${col}.testAction`)}
                  title={t(`${col}.testAction`)}
                  onClick={() => {
                    onTest(row);
                  }}
                >
                  <Send className="h-4 w-4" aria-hidden="true" />
                </Button>
                <Button
                  size="icon"
                  variant="ghost"
                  aria-label={t(`${col}.editAction`)}
                  title={t(`${col}.editAction`)}
                  onClick={() => {
                    onEdit(row);
                  }}
                >
                  <Pencil className="h-4 w-4" aria-hidden="true" />
                </Button>
                <Button
                  size="icon"
                  variant="ghost"
                  aria-label={t(`${col}.deleteAction`)}
                  title={t(`${col}.deleteAction`)}
                  loading={pendingDeleteId === row.id}
                  onClick={() => {
                    onDelete(row);
                  }}
                >
                  <Trash2 className="h-4 w-4" aria-hidden="true" />
                </Button>
              </div>
            </DataTableTd>
          </DataTableTr>
        ))}
      </DataTableTbody>
    </DataTableTable>
  );
}
